import { IAPICodeDTO, ItemValue } from '$/types'
import { selectDataLabel } from 'common/utils/chart/chartUtils'

import {
  IChartData,
  IChartDataValues,
  IQRAChartBaseObject,
  IQRAChartObjects,
  IQRALinearReferenceChartObject,
  IQRAStandardChartObject,
  QRAGraphType,
  IChartSeriesLabels
} from '.'

import { IThreshold } from './Implementations/GraphLinearReference'

export const wasRiskLevelFiltered = (
  idx: number,
  riskLevels: Array<IAPICodeDTO>,
  categories: Array<string>
) => {
  if (riskLevels.length === 0) {
    return false
  }

  const levelsFiltered = riskLevels.map((level) => level.name)

  return !levelsFiltered.includes(categories[idx])
}

export const filterS2Values = (
  s1Values: number[],
  s2Values: number[],
  riskLevels: Parameters<typeof wasRiskLevelFiltered>[1],
  categories: Parameters<typeof wasRiskLevelFiltered>[2]
): number[] => {
  const s2NonFilteredValues: number[] = s2Values

  s1Values.forEach((value, idx) => {
    /*
        if some s1Value have 0 risk, but then we run WIS (s2Value) turning up pipe pressure and risk appears
        we shouldn't hide risks in those scenarios
      */
    if (
      value ||
      (!value && !wasRiskLevelFiltered(idx, riskLevels, categories))
    ) {
      /*
          use a sparsed array not to loose order when is important,
          as an example to match against colors like Risk Level
        */
      s2NonFilteredValues[idx] = s2Values[idx]
    } else {
      s2NonFilteredValues[idx] = 0
    }
  })

  return s2NonFilteredValues
}

export const getCategoryDataForState = (
  chart: IQRAChartBaseObject,
  stateName: ItemValue
): number[] => {
  let stateCategoryValues: number[] = []

  switch (chart.graphType) {
    case QRAGraphType.LINEAR_REFERENCE: {
      let prevSegmentToMeasure: number | undefined

      stateCategoryValues = (
        chart as IQRALinearReferenceChartObject
      ).items.reduce((accumulator: number[], category) => {
        let res: number | undefined

        // Checks if theres gaps in the pipe segments and inserts dummy value to be replaced by null later
        // Shouldn't replace with null now as it affects the other charts that we have
        if (
          prevSegmentToMeasure &&
          prevSegmentToMeasure !== category.values[0].value.fromMeasure
        ) {
          accumulator.push(-1)
        }

        category.values.forEach((v) => {
          if (v.key === stateName.key) {
            if (res != null) {
              console.warn('Duplicate state found in chart object', category.id)
            }

            res = v.value.risk
          }
        })
        // If state does not exist for a category, push placeholder `0` to it..
        accumulator.push(res ?? 0)
        prevSegmentToMeasure = category.values[0].value.toMeasure

        return accumulator
      }, stateCategoryValues)

      break
    }

    default: {
      ;(chart as IQRAStandardChartObject).items.forEach((category) => {
        let res: number | undefined

        category.values.forEach((v: { key: string; value: number }) => {
          if (v.key === stateName.key) {
            if (res != null) {
              console.warn(
                'Duplicate state found in chart object',
                category.name
              )
            }

            res = v.value
          }
        })
        // If state does not exist for a category, push placeholder `0` to it..
        stateCategoryValues.push(res ?? 0)
      })
    }
  }

  return stateCategoryValues
}

export const getFormattedChartValues = (
  chart: IQRAChartBaseObject,
  stateNames: ItemValue[]
) => {
  const seriesValues: IChartDataValues = {}
  const seriesLabels: IChartSeriesLabels = {}

  for (const stateName of stateNames) {
    const _key = stateName.key

    seriesValues[_key] = getCategoryDataForState(chart, stateName)
    seriesLabels[_key] = selectDataLabel(stateName)
  }

  return { seriesValues, seriesLabels }
}

/**
 * NOTE: Only use this method for simple charts (i.e. basic line / bar / pie chart)
 */
export const getStandardChartCVPairs = (
  charts: IQRAChartObjects,
  chartType: QRAGraphType
): IChartData => {
  const chart = charts[chartType] as IQRAStandardChartObject

  if (chart == null || chart.items.length < 1) {
    return {
      categoryIds: [],
      categories: [],
      values: {},
      labels: {}
    }
  }

  /**
   * NOTE: Used for select interaction (routing) supported in some charts,
   * may need to improve/overhaul parameter structure especially for multi
   * series support for What-If scenario result review..
   */
  const categoryIds = chart.items.map((i) => i.id)
  const categories = chart.items.map((i) => i.name)

  // NOTE: Name can be dynamic in saved state / compare view
  const stateNames = chart.items.reduce((arr: ItemValue[], cur) => {
    for (const categoryValue of cur.values) {
      if (!arr.find((cat) => cat.key === categoryValue.key)) {
        arr.push(categoryValue)
      }
    }

    return arr
  }, [])

  const { seriesValues, seriesLabels } = getFormattedChartValues(
    chart,
    stateNames
  )

  /**
   * NOTE: Graph component structure example
   * categoryIds = [1, 2, ...]
   * categories = ['External Corrosion', 'Internal Corrosion', ...]
   * values = {
   *   state1 = ['External Corrosion value', 'Internal Corrosion Value', ...]
   *   state2 = ['External Corrosion value', 'Internal Corrosion Value', ...]
   * }
   */
  return { categoryIds, categories, values: seriesValues, labels: seriesLabels }
}

export const getLRGraphData = (charts: any, chartType: QRAGraphType) => {
  const XValues: number[] = []
  const measureValues: { fromMeasure: number; toMeasure: number }[] = []
  let threshold: IThreshold[] = []
  const chart = charts[chartType]

  if (chart == null || chart.items.length < 1) {
    return {
      XValues: [],
      threshold: [
        { id: 0, code: '', name: '', minValue: 0, maxValue: 0, colour: '' }
      ],
      measureValues: [],
      seriesValues: {},
      seriesLabels: {}
    }
  }

  const stateNames = chart.items.reduce((arr: ItemValue[], cur: any) => {
    for (const categoryValue of cur.values) {
      if (!arr.find((cat) => cat.key === categoryValue.key)) {
        arr.push(categoryValue)
      }
    }

    return arr
  }, [])

  const { seriesValues, seriesLabels } = getFormattedChartValues(
    chart,
    stateNames
  )

  threshold = chart.acceptanceCategories

  for (let i = 0; i < chart?.items?.length; i++) {
    const fromMeasure = chart?.items[i].values[0].value.fromMeasure
    const toMeasure = chart?.items[i].values[0].value.toMeasure

    measureValues.push({ fromMeasure, toMeasure })

    if (i == 0) {
      XValues.push(fromMeasure)
    }

    if (i < chart?.items?.length - 1) {
      const nextFromMeasure = chart?.items[i + 1].values[0].value.fromMeasure
      const currentToMeasure = chart?.items[i].values[0].value.toMeasure

      if (currentToMeasure === nextFromMeasure) {
        XValues.push(currentToMeasure)
      } else {
        XValues.push(currentToMeasure)
        XValues.push(nextFromMeasure)

        measureValues.push({
          fromMeasure: currentToMeasure,
          toMeasure: nextFromMeasure
        })

        if (stateNames.length === 2) {
          const nextToMeasure = chart.items[i + 1].values[0].value.toMeasure

          measureValues.push({
            fromMeasure: nextFromMeasure,
            toMeasure: nextToMeasure
          })
        }
      }
    } else {
      XValues.push(toMeasure)
    }
  }

  return { XValues, seriesValues, threshold, seriesLabels, measureValues }
}
